import { Controller } from "@hotwired/stimulus"

/*
  Combobox / autocomplete z chipsami (badge). Jeden kontroler, dwa tryby:
  - pojedynczy wybór (województwo, powiat) → 1 chip zastępuje input, 1 hidden input
  - multiselect (podkategorie)             → N chipsów, N hidden inputów o tej samej
    nazwie (Django: request.POST.getlist("subcategories"))
  - zależność powiat → województwo: data-combobox-depends-on-value="voivodeship";
    opcje zależnego pola mają "group" = value rodzica. Zmiana rodzica czyści wybór.

  Kontrakt opcji (JSON w data-combobox-options-value):
    [{ "value": 1, "label": "mazowieckie", "meta": "8 pow.", "group": null }]

  Klawiatura: ↑/↓ nawigacja, Enter wybór, Esc zamyka, Backspace (pusty input)
  usuwa ostatni chip.

  Emituje zdarzenie "combobox:change" na window — do wieszania kolejnych zależności
  (np. wizard nasłuchuje podkategorii → odświeża hint cenowy).

  selectValues(values) — publiczny setter do preselekcji z zewnątrz (np. modal edycji
  wypełniany danymi z API): zastępuje wybór po wartościach, bez ręcznego budowania opcji.
*/
export default class extends Controller {
  static targets = ["box", "input", "list", "chips", "hidden"]
  static values = {
    name: String,
    multiple: { type: Boolean, default: false },
    options: { type: Array, default: [] },
    dependsOn: { type: String, default: "" },
    placeholder: { type: String, default: "Szukaj…" },
    disabledPlaceholder: { type: String, default: "Najpierw wybierz pole nadrzędne" },
    max: { type: Number, default: 0 },
    required: { type: Boolean, default: false },
  }

  connect() {
    this.selected = []
    this.hi = 0
    this.groupFilter = null
    if (this.dependsOnValue) {
      this.onParentChange = (e) => {
        if (e.detail.name !== this.dependsOnValue) return
        this.groupFilter = e.detail.value || null
        this.clearAll()
        this.refreshDisabled()
      }
      window.addEventListener("combobox:change", this.onParentChange)
    }
    this.refreshDisabled()
    this.renderChips()
  }

  disconnect() {
    if (this.onParentChange) window.removeEventListener("combobox:change", this.onParentChange)
  }

  // ---- stan pomocniczy ----
  get isDisabled() { return !!this.dependsOnValue && !this.groupFilter }
  get pool() {
    let opts = this.optionsValue
    if (this.dependsOnValue) opts = opts.filter(o => o.group === this.groupFilter)
    return opts.filter(o => !this.selected.some(s => s.value === o.value))
  }
  norm(s) { return s.toLowerCase().replace(/ł/g, "l").normalize("NFD").replace(/[̀-ͯ]/g, "") }
  results() {
    const q = this.norm(this.inputTarget.value.trim())
    return this.pool.filter(o => this.norm(o.label).includes(q)).slice(0, 8)
  }

  // ---- akcje (data-action) ----
  focusInput() { if (!this.isDisabled) this.inputTarget.focus() }
  open() { this.hi = 0; this.renderList(true) }
  close() { setTimeout(() => { this.listTarget.hidden = true }, 140) }
  search() { this.hi = 0; this.renderList(true) }
  // Przestawia podświetlenie na istniejących wierszach — bez przebudowy listy.
  highlight(index) {
    this.hi = index
    const rows = this.listTarget.querySelectorAll(".ls-combo__opt")
    rows.forEach((row, i) => row.classList.toggle("is-hi", i === index))
  }
  keydown(e) {
    const res = this.results()
    if (e.key === "ArrowDown") { e.preventDefault(); this.highlight(Math.min(this.hi + 1, res.length - 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); this.highlight(Math.max(this.hi - 1, 0)) }
    else if (e.key === "Enter") { e.preventDefault(); if (!this.listTarget.hidden && res[this.hi]) this.pick(res[this.hi]) }
    else if (e.key === "Escape") { this.listTarget.hidden = true }
    else if (e.key === "Backspace" && !this.inputTarget.value && this.selected.length) {
      this.remove(this.selected[this.selected.length - 1].value)
    }
  }

  // ---- logika wyboru ----
  pick(opt) {
    if (this.multipleValue && this.maxValue && this.selected.length >= this.maxValue) return
    this.selected = this.multipleValue ? [...this.selected, opt] : [opt]
    this.inputTarget.value = ""
    this.renderChips()
    this.renderList(this.multipleValue) // multiselect: lista zostaje otwarta
    if (!this.multipleValue) this.inputTarget.blur()
    this.emit()
  }
  remove(value) {
    this.selected = this.selected.filter(s => s.value !== value)
    this.renderChips()
    this.emit()
    if (!this.multipleValue) this.inputTarget.focus()
  }
  clearAll() { this.selected = []; this.inputTarget.value = ""; this.renderChips(); this.emit() }

  // Zaznaczenie z zewnątrz po wartościach (np. wypełnienie formularza edycji z API) —
  // mapuje id-ki na opcje z optionsValue (już wczytane server-side, bez fetcha), zastępuje
  // cały wybór, renderuje chipy i emituje combobox:change tak jak pick()/clearAll().
  // Nieznane wartości (spoza optionsValue) są pomijane.
  selectValues(values) {
    const list = Array.isArray(values) ? values : []
    const found = list
      .map(v => this.optionsValue.find(o => String(o.value) === String(v)))
      .filter(Boolean)
    this.selected = this.multipleValue ? found : found.slice(0, 1)
    this.inputTarget.value = ""
    this.renderChips()
    this.emit()
  }
  emit() {
    window.dispatchEvent(new CustomEvent("combobox:change", {
      detail: {
        name: this.nameValue,
        value: this.multipleValue ? this.selected.map(s => s.value) : (this.selected[0]?.value ?? null),
      }
    }))
  }

  // ---- render ----
  refreshDisabled() {
    const dis = this.isDisabled
    this.inputTarget.disabled = dis
    this.boxTarget.classList.toggle("is-disabled", dis)
    this.inputTarget.placeholder = dis ? this.disabledPlaceholderValue : this.placeholderValue
  }

  renderChips() {
    // Czyścimy zawartość chipów i hidden inputów — textContent zamiast innerHTML (XSS)
    this.chipsTarget.textContent = ""
    this.hiddenTarget.textContent = ""
    for (const s of this.selected) {
      const chip = document.createElement("span")
      chip.className = "ls-chip"

      const labelSpan = document.createElement("span")
      labelSpan.className = "ls-chip__label"
      labelSpan.textContent = s.label
      chip.appendChild(labelSpan)

      if (s.meta) {
        const metaSpan = document.createElement("span")
        metaSpan.className = "ls-chip__meta"
        metaSpan.textContent = s.meta
        chip.appendChild(metaSpan)
      }

      const xBtn = document.createElement("button")
      xBtn.type = "button"
      xBtn.className = "ls-chip__x"
      xBtn.setAttribute("aria-label", "Usuń")
      xBtn.textContent = "×"
      xBtn.addEventListener("mousedown", (e) => { e.preventDefault(); this.remove(s.value) })
      chip.appendChild(xBtn)

      this.chipsTarget.appendChild(chip)

      const hid = document.createElement("input")
      hid.type = "hidden"
      hid.name = this.nameValue
      hid.value = s.value
      this.hiddenTarget.appendChild(hid)
    }
    const singleSelected = !this.multipleValue && this.selected.length > 0
    this.boxTarget.classList.toggle("is-selected", singleSelected)
    this.inputTarget.hidden = singleSelected // chip zastępuje input w trybie single
    if (!this.isDisabled) {
      this.inputTarget.placeholder = this.selected.length ? "Dodaj kolejną…" : this.placeholderValue
    }
    if (!this.multipleValue && !this.isDisabled) {
      this.inputTarget.placeholder = this.placeholderValue
    }
  }

  renderList(show) {
    const res = this.results()
    // Czyścimy listę — textContent zamiast innerHTML (XSS)
    this.listTarget.textContent = ""
    res.forEach((o, i) => {
      const row = document.createElement("div")
      row.className = "ls-combo__opt" + (i === this.hi ? " is-hi" : "")

      const labelSpan = document.createElement("span")
      labelSpan.className = "ls-combo__opt-label"
      labelSpan.textContent = o.label
      row.appendChild(labelSpan)

      if (o.meta) {
        const metaSpan = document.createElement("span")
        metaSpan.className = "ls-combo__meta"
        metaSpan.textContent = o.meta
        row.appendChild(metaSpan)
      }

      row.addEventListener("mousedown", (e) => { e.preventDefault(); this.pick(o) })
      // Najechanie TYLKO przestawia podświetlenie. Przebudowa listy tutaj podmieniałaby
      // węzeł pod kursorem, więc mousedown trafiał w element odpięty od DOM i wybór ginął.
      row.addEventListener("mouseenter", () => this.highlight(i))
      this.listTarget.appendChild(row)
    })
    if (!res.length) {
      const empty = document.createElement("div")
      empty.className = "ls-combo__empty"
      empty.textContent = "Brak wyników"
      this.listTarget.appendChild(empty)
    }
    this.listTarget.hidden = !show || this.isDisabled
  }
}
